import _ from 'lodash';

import { M4, V4, debug } from '@moleculeone/utils';

import * as config from '../config';
import { positionsDataTest } from '../checks';
import { randomElem } from '../collections';
import {
  overlapScore,
  isGroupKey,
  mergeGroups,
  entropyDistribution,
  shuffle,
  cyclePermutations,
  percent,
} from './utils';

const DEFAULT_ANGLE = (120 * Math.PI) / 180;

const angleSetsPerChildCount = {
  2: [[120, 120]],
  3: [
    [120, 120, 120],
    [120, 110, 130],
  ],
  4: [
    [90, 90, 90, 90],
    [120, 90, 60, 90],
  ],
  5: [
    [72, 72, 72, 72, 72],
    [90, 90, 60, 60, 60],
  ],
  6: [
    [60, 60, 60, 60, 60, 60],
    [54, 54, 72, 54, 54, 72],
  ],
};

const angleSetsWithCyclicPermutations = {};

// after drawing all circles, what's left is a set of disjoint trees
// we're drawing them one by one here.
export function mergeGroupTrees(random, positionsData, params = {}) {
  let roots = Object.keys(positionsData.groupGraph)
    .filter(k => !isGroupKey(k))
    .filter(k => positionsData.groupGraph[k].length > 1) // atoms in more than one group
    .map(k => positionsData.groupGraph[k][0]);
  while (roots.length > 0) {
    drawTree(random, positionsData, roots[0], params);
    roots = Object.keys(positionsData.groupGraph)
      .filter(k => !isGroupKey(k))
      .filter(k => positionsData.groupGraph[k].length > 1)
      .map(k => positionsData.groupGraph[k][0]);
  }
  debug(positionsDataTest, positionsData);
}

export function drawTree(random, positionsData, rootGroupIdx, params = {}) {
  const mol = positionsData.mol;
  const positions = Object.values(positionsData.groupPositions[rootGroupIdx]);
  const center = V4.average(...positions);
  const radius = Math.max(...positions.map(pos => V4.dist(pos, center)));
  positionsData.treeParams[rootGroupIdx] = {
    parentPivotIdx: null,
    parentGroupIdx: null,
    depth: 0,
    alignMatrix: M4.id, // matrix that aligns this group to it's parent group
    cumulativeMatrix: M4.id, // similar purpose, but alignment occurs in root's coordinate space
    radius,
    center,
    alignedCenter: center,
  };

  // precompute cyclic permutations of angle sets
  Object.keys(angleSetsPerChildCount).forEach(el => {
    const obj = angleSetsPerChildCount[el];
    const cp = obj.map(cyclePermutations);
    const ucp = cp.map(el => _.uniqBy(el, JSON.stringify));

    angleSetsWithCyclicPermutations[el] = _.flatten(ucp);
  });

  positionsData.treePositions = {};
  positionsData.treePositions[rootGroupIdx] = positionsData.groupPositions[rootGroupIdx];
  forEachSuccessor(positionsData, null, null, rootGroupIdx, addTreeStructure);
  forEachSuccessor(positionsData, null, null, rootGroupIdx, (...args) => buildAlignMatrix(random, ...args));
  forEachSuccessor(positionsData, null, null, rootGroupIdx, buildPositions);

  let triesCount = 0;
  let triesSinceImprovement = 0;
  let [bestScore, badGroup1, badGroup2] = totalOverlapScore(positionsData);
  let bestPositions = positionsData.treePositions;
  const totalMaxTries = params.totalMax || config.drawing.totalTreeRefinements;
  const maxTriesSinceImprovement = params.changeMax || config.drawing.badTreeRefinements;
  // console.log("MAX:", maxTriesSinceImprovement);
  while (
    triesCount < totalMaxTries &&
    triesSinceImprovement < maxTriesSinceImprovement &&
    bestScore < 1 // best[0] = score - there's no point in improving results further
  ) {
    const subTree = collectBranches(positionsData, badGroup1, badGroup2);
    const nearestRoot = subTree.pop();
    for (const group of subTree) {
      const parentIdx = positionsData.treeParams[group].parentGroupIdx;
      const parentPivot = positionsData.treeParams[group].parentPivotIdx;
      buildGroupAlignMatrix(
        random,
        positionsData,
        parentIdx,
        parentPivot,
        group,
        triesCount,
        Math.min(totalMaxTries, maxTriesSinceImprovement)
      );
    }
    const rootParent = positionsData.treeParams[nearestRoot].parentGroupIdx;
    const rootPivot = positionsData.treeParams[nearestRoot].parentPivotIdx;
    forEachSuccessor(positionsData, rootParent, rootPivot, nearestRoot, buildPositions);

    const group1Pos = positionsData.treePositions[badGroup1];
    const group2Pos = positionsData.treePositions[badGroup2];
    const pairScore = overlapScore(mol, group1Pos, group2Pos, positionsData.bondArrays);
    // don't look for bad group overlaps if we haven't corrected ones that we're supposed to
    if (pairScore >= bestScore) {
      // sometimes, we have to change positions even if result is worse
      // because we may end up in some local minimum
      const threshold = 0.5;
      const overlapData = totalOverlapScore(positionsData, Math.max(bestScore, threshold));
      if (overlapData[0] > bestScore || bestScore < threshold) {
        [bestScore, badGroup1, badGroup2] = overlapData;
        bestPositions = { ...positionsData.treePositions };
        triesSinceImprovement = 0;
      }
    }
    triesCount += 1;
    triesSinceImprovement += 1;
  }

  mergeGroups(positionsData, Object.values(bestPositions), Object.keys(bestPositions));
}

function totalOverlapScore(positionsData, threshold = 0) {
  const mol = positionsData.mol;
  let overlappingGroups = [null, null];
  let worstScore = Number.POSITIVE_INFINITY;
  const groupIndices = Object.entries(positionsData.treePositions);
  for (let i = 0; i < groupIndices.length - 1; i++) {
    const [group1Idx, group1Pos] = groupIndices[i];
    const g1 = positionsData.treeParams[group1Idx];
    for (let j = i + 1; j < groupIndices.length; j++) {
      const [group2Idx, group2Pos] = groupIndices[j];
      const g2 = positionsData.treeParams[group2Idx];
      const dist = V4.dist(g1.alignedCenter, g2.alignedCenter) - g1.radius - g2.radius;
      if (dist > 1) {
        continue;
      }
      let score = overlapScore(mol, group1Pos, group2Pos, positionsData.bondArrays);

      const angles = Object.keys(positionsData.treeParams).map(key => {
        const el = positionsData.treeParams[key];
        return el.angle || DEFAULT_ANGLE;
      });

      const angleVarietyScore = entropyDistribution(angles);

      score -= angleVarietyScore * (1 / angles.length);

      if (score < worstScore) {
        worstScore = score;
        overlappingGroups = [group1Idx, group2Idx];
      }
      if (score < threshold) {
        return [worstScore, ...overlappingGroups];
      }
    }
  }
  return [worstScore, ...overlappingGroups];
}

// returns list containing group indices that form path in tree from group1 to group2
// last item is the nearest common root for these groups
function collectBranches(positionsData, group1Idx, group2Idx) {
  const branchIndices = [];
  while (group1Idx !== group2Idx) {
    if (positionsData.treeParams[group1Idx].depth > positionsData.treeParams[group2Idx].depth) {
      branchIndices.push(group1Idx);
      group1Idx = positionsData.treeParams[group1Idx].parentGroupIdx;
    } else {
      branchIndices.push(group2Idx);
      group2Idx = positionsData.treeParams[group2Idx].parentGroupIdx;
    }
  }
  branchIndices.push(group1Idx);
  return branchIndices;
}

function forEachSuccessor(positionsData, parentGroupIdx, parentCurrentPivot, currentGroupIdx, fun) {
  for (const currentChildPivot of positionsData.groupGraph[currentGroupIdx]) {
    if (currentChildPivot !== parentCurrentPivot) {
      for (const childGroupIdx of positionsData.groupGraph[currentChildPivot]) {
        if (childGroupIdx !== currentGroupIdx) {
          fun(positionsData, currentGroupIdx, currentChildPivot, childGroupIdx);
          forEachSuccessor(positionsData, currentGroupIdx, currentChildPivot, childGroupIdx, fun);
        }
      }
    }
  }
}

function addTreeStructure(positionsData, parentGroupIdx, parentChildPivot, childGroupIdx) {
  const positions = Object.values(positionsData.groupPositions[childGroupIdx]);
  const center = V4.average(...positions);
  const radius = Math.max(...positions.map(pos => V4.dist(pos, center)));
  positionsData.treeParams[childGroupIdx] = {
    parentPivotIdx: parentChildPivot,
    parentGroupIdx,
    depth: parentGroupIdx == null ? 0 : positionsData.treeParams[parentGroupIdx].depth + 1,
    radius,
    center,
  };
}

function getGraphNeighbors(graph, pivotId, currentId) {
  return graph[pivotId].filter(el => el !== currentId);
}

function buildGroupAlignMatrix(
  random,
  positionsData,
  parentGroupIdx,
  parentChildPivot,
  childGroupIdx,
  tries,
  maxTries
) {
  const bonds = positionsData.neighbors;
  const fanSize = bonds[parentChildPivot].length;

  let angleSet;

  // naive strategy implementation - fall back to less optimal angle set
  // based on number of iterations
  if (percent(tries, maxTries) < 30) {
    const angleSetCandidates = angleSetsPerChildCount[fanSize];
    angleSet = angleSetCandidates[0];
  } else if (percent(tries, maxTries) < 50) {
    const angleSetCandidates = angleSetsPerChildCount[fanSize];
    angleSet = angleSetCandidates[tries % angleSetCandidates.length];
  } else if (percent(tries, maxTries) < 90) {
    const angleSetCandidates = angleSetsWithCyclicPermutations[fanSize];
    angleSet = angleSetCandidates[tries % angleSetCandidates.length];
  } else {
    const angleSetCandidates = angleSetsWithCyclicPermutations[fanSize];
    angleSet = randomElem(random, angleSetCandidates);
  }

  const neighbors = shuffle(getGraphNeighbors(positionsData.groupGraph, parentChildPivot, parentGroupIdx), random);

  for (let i = 0; i < neighbors.length; i++) {
    const angle = angleSet.slice(0, i + 1).reduce((acc, curr) => {
      return acc + curr;
    }, 0);

    const angleRad = (angle * Math.PI) / 180;
    buildAlignMatrix(random, positionsData, parentGroupIdx, parentChildPivot, neighbors[i], angleRad);
  }
}

function buildAlignMatrix(random, positionsData, parentGroupIdx, parentChildPivot, childGroupIdx, angle) {
  angle = angle || DEFAULT_ANGLE;

  const parentPositions = positionsData.groupPositions[parentGroupIdx];
  const childPositions = positionsData.groupPositions[childGroupIdx];

  const bonds = positionsData.neighbors;
  const flip = random.quick() >= 0.5;

  const parentRefPos = pickRefPos(random, bonds, parentChildPivot, parentPositions, true);
  const childRefPos = pickRefPos(random, bonds, parentChildPivot, childPositions, false);

  const parentPivotPos = parentPositions[parentChildPivot];
  const childPivotPos = childPositions[parentChildPivot];

  positionsData.treeParams[childGroupIdx].alignMatrix = alignMatrix(
    parentPivotPos,
    parentRefPos,
    childPivotPos,
    childRefPos,
    angle,
    flip
  );
  positionsData.treeParams[childGroupIdx].angle = angle;
}

function buildPositions(positionsData, parentGroupIdx, parentChildPivot, childGroupIdx) {
  const alignParentToRoot = positionsData.treeParams[parentGroupIdx].cumulativeMatrix;
  const alignChildToParent = positionsData.treeParams[childGroupIdx].alignMatrix;
  const alignChildToRoot = M4.mulM(alignParentToRoot, alignChildToParent);
  const treeParams = positionsData.treeParams[childGroupIdx];
  const positions = positionsData.groupPositions[childGroupIdx];
  treeParams.cumulativeMatrix = alignChildToRoot;
  treeParams.alignedCenter = M4.mulV(alignChildToRoot, treeParams.center);
  positionsData.treePositions[childGroupIdx] = M4.map(alignChildToRoot, positions);
}

function pickRefPos(random, bonds, pivotAtomIdx, groupPositions, parent) {
  const nborPoints = bonds[pivotAtomIdx].map(([b, a]) => groupPositions[a.idx]).filter(pos => pos != null);

  if (nborPoints.length === 2) {
    const pivotPos = groupPositions[pivotAtomIdx];
    const massCenter = V4.average(...nborPoints.map(p => V4.sub(p, pivotPos)));
    const p1 = nborPoints[0];
    const p2 = nborPoints[1];
    const angle1 = V4.angleZ(V4.sub(p1, pivotPos), massCenter);
    const angle2 = V4.angleZ(V4.sub(p2, pivotPos), massCenter);
    if (angle1 < angle2 !== parent) {
      return p1;
    }
    return p2;
  }

  return randomElem(random, nborPoints);
}

// matrix that moves child so that it's pivot position matches parent's pivot position,
// angle between pivot-childRefPos and pivot-parentRefPos lines equals angle parameter,
// and optionally flips the child
export function alignMatrix(parentPivotPos, parentRefPos, childPivotPos, childRefPos, angle, flip) {
  const parentDir = V4.sub(parentRefPos, parentPivotPos);
  const childDir = V4.sub(childRefPos, childPivotPos);

  return M4.mulM(
    M4.moveTo(parentPivotPos),
    M4.rotZ(V4.angleZ(parentDir, V4.vectx) + angle),
    flip ? M4.flipY : M4.id,
    M4.rotZ(V4.angleZ(V4.vectx, childDir)),
    M4.moveFrom(childPivotPos)
  );
}
